import { Components } from '@/api/generated/api';
import { COLOR_GREY, COLOR_PRIMARY, COLOR_SUCCESS, COLOR_WARNING } from '@/constants';
import { type GraduatedSeatTier } from 'common/utils/billing.utils';
import { Component as VueComponent } from 'vue';

export type BillingInterval = 'year' | 'month';

export type CheckoutPaymentContext = {
  expressPaymentType?: string;
};

export type CheckoutSubscriptionSession = {
  clientSecret: string;
  billingSubscriptionId: string;
  returnUrl: string;
};

export enum BillingPageTab {
  Subscription = 'subscription',
  Invoices = 'invoices',
}

export type BillingPrice = {
  amountCents: number;
  perSeatCents?: number;
  seatTiers?: GraduatedSeatTier[];
};

export type MonthlyBillingPrice = {
  amountCents: number;
  perSeatCents?: number;
};

/**
 * Per-market amounts of one product, sourced EXCLUSIVELY from the Stripe price catalog
 * (`GET /billing/products/pricing`) — the app hardcodes no amounts and no currency anywhere.
 * A null interval means "not offered in this market".
 */
export type ProductMarketPricing = {
  currency: string;
  yearly: BillingPrice | null;
  monthly: MonthlyBillingPrice | null;
};

export type ProductUIConfig = {
  icon: VueComponent;
  titleKey: string;
  descriptionKey: string;
  pricingCalculatorDescriptionKey: string;
  pricingLabelKey: string;
  highlightKey?: string;
  highlightColor?: string;
};

export type ProductWithMeta = Components.Schemas.ProductTypeDto & {
  meta: NonNullable<ProductUIConfig | undefined>;
  /** Null when this market has no price for the product (purchase surfaces drop it; legacy display tolerates it). */
  pricing: ProductMarketPricing | null;
  priceLabel: string;
  originalPriceLabel?: string;
  appliedDiscountPercent?: number;
};

export type CheckoutPriceDisplay = {
  priceLabel: string;
  originalPriceLabel?: string;
  detailLabel: string;
};

export type BillingInvoiceStatusPresentation = {
  color: string;
  label: string;
  theme: 'fill' | 'outline' | 'transparent';
};

export const BILLING_STATUS_COLOR_PRIMARY = COLOR_PRIMARY;
export const BILLING_STATUS_COLOR_SUCCESS = COLOR_SUCCESS;
export const BILLING_STATUS_COLOR_WARNING = COLOR_WARNING;
export const BILLING_STATUS_COLOR_MUTED = COLOR_GREY;
